import { Edit, Save } from '@mui/icons-material';
import { Box, Button, Chip, IconButton, TextField } from '@mui/material';
import { FC, useCallback, useMemo, useState } from 'react';
import { useCreateUserByUserIdJobFunctionByJobFunctionIdTagsMutation } from '../../store/generated/generatedApi';
import { useFailedActionSnackbar, useSuccessfulActionSnackbar } from '../CoreLib/library';

export interface IUserJobFunctionTagManagerProps {
    userId: string;
    jobFunctionId: string;
    initialTags: string[];
}

export const UserJobFunctionTagManager: FC<IUserJobFunctionTagManagerProps> = ({ userId, jobFunctionId, initialTags }) => {
    const [isEditMode, setIsEditMode] = useState(false);
    const [tags, setTags] = useState<string[]>(initialTags);
    const [newTagInput, setNewTagInput] = useState('');
    const [saveUserJobFunctionTags, { isLoading, isSuccess, isError, reset }] = useCreateUserByUserIdJobFunctionByJobFunctionIdTagsMutation();
    useSuccessfulActionSnackbar('set', 'user\'s tags', isSuccess, reset);
    useFailedActionSnackbar('set', 'user\'s tags', isError, reset);

    const handleSaveClicked = useCallback(() => {
        if (isLoading) {
            return;
        }
        saveUserJobFunctionTags({ params: { userId, jobFunctionId }, payload: tags }).unwrap().then(() => {
            setIsEditMode(false);
        });
    }, [isLoading, saveUserJobFunctionTags, tags, userId, jobFunctionId]);

    const isInputAValidTag = useMemo(() => !(!newTagInput || newTagInput.trim() === '' || tags.includes(newTagInput)), [newTagInput, tags]);

    const handleAddTagClicked = useCallback(() => {
        if (!isInputAValidTag) {
            return;
        }
        
        setTags([...tags, newTagInput]);
        setNewTagInput('');
    }, [isInputAValidTag, newTagInput, tags]);

    return (
        <Box display='flex' flexDirection='column' flexGrow={1} gap={1} onClick={(e) => isEditMode && e.stopPropagation()}>
            <Box display='flex' flexWrap='nowrap' gap={1} alignItems='center'>
                <Box display='flex' flexWrap='wrap' gap={1} flexGrow={1}>
                    {tags.length === 0 && <span style={{ fontStyle: 'italic' }}>No tags</span>}
                    {[...tags].sort().map((tag) => (
                        <Chip label={tag} onDelete={isEditMode ? () => setTags(tags.filter((t) => t !== tag)) : undefined} />
                    ))}
                </Box>
                {!isEditMode && (
                    <Box>
                        <IconButton
                            onClick={e => {
                                e.stopPropagation();
                                setIsEditMode(!isEditMode);
                            }}
                            size='small'>
                            <Edit />
                        </IconButton>
                    </Box>
                )}
            </Box>
            {isEditMode && (
                <>
                    <Box display='flex' flexWrap='nowrap' gap={1}>
                        <TextField
                            value={newTagInput}
                            onChange={(e) => setNewTagInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleAddTagClicked();
                                }
                            }}
                            fullWidth
                            size='small'
                            disabled={isLoading}
                        />
                        <Button
                            onClick={handleAddTagClicked}
                            variant='contained'
                            size='small'
                            disabled={isLoading || !isInputAValidTag}>
                            Add
                        </Button>
                    </Box>
                    <Box>
                        <Button
                            onClick={handleSaveClicked}
                            startIcon={<Save />}
                            variant='contained'
                            size='small'
                            fullWidth
                            disabled={isLoading}>
                            Save
                        </Button>
                    </Box>
                </>
            )}
        </Box>
    );
};
